import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {DeviceOutput} from "../../devices/models/device_output";
import {Subject, distinctUntilChanged, map, switchMap, takeUntil} from "rxjs";
import {BasePageComponent} from "../../shared/components/base-page.component";
import {LocalizerService, ScreenService, v2CallVersion} from "../../shared";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {DashboardDeviceShadow} from "../../shared/components/base-dashboard.component";
import { Versioning } from "../../shared/utils/versioning";
import {CallEntity, CallEntityKind, CallParticipant} from "../../shared/models/webrtc/call/v2/call-entity";
import {Session} from "../../shared/models/webrtc/call/call";
import {CallSessionManager} from "../../shared/services/call-session-manager.service";
import { Store } from '@ngrx/store';
import { CurrentCallActions } from '../../state/call/current-call.actions';
import { selectCurrentCall } from '../../state/call/current-call.selector';
import { CallEntry, CallLeg, CallSession } from '../../state/call/current-call-state';
import { MediaStreamStore } from '../../shared/services/media-stream-store.service';

@Component({
    selector: 'app-device-live-view',
    templateUrl: './device-live-view.component.html',
    styleUrl: './device-live-view.component.scss',
    animations: [
        trigger('curtainOpen', [
            state('hide', style({
                clipPath: 'circle(0% at 50% 50%)',
                opacity: 0,
                transform: 'scale(0.98)'
            })),
            state('show', style({
                clipPath: 'circle(100% at 50% 50%)',
                opacity: 1,
                transform: 'scale(1)'
            })),
            transition('show => hide', [
                animate('500ms cubic-bezier(.2,.9,.2,1)')
            ]),
            transition('hide => show', [
                animate('400ms cubic-bezier(.2,.9,.2,1)')
            ])
        ])
    ]
})
export class DeviceLiveViewComponent extends BasePageComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

    @Input() override isBusy: boolean = false;

    @Input() entityId: string | null = null;

    @Input() entityKind: CallEntityKind | null = null;

    @Input() installationId: string | null = null;

    @Input() placeholderUrl: string | null = null;

    @Input() set shadowUpdates(updates: DashboardDeviceShadow[]) {
        this.shadows = updates;
    }

    @ViewChild('videoElem') videoRef!: ElementRef<HTMLVideoElement>;
    @ViewChild('secondVideoElem') secondVideoRef!: ElementRef<HTMLVideoElement>;

    private shadows: DashboardDeviceShadow[] = [];
    private destroy$ = new Subject<void>();
    private entitySub$ = new Subject<void>();
    private viewInitialized = false;
    private callState: CallEntry | null = null;

    protected isAudioMuted: boolean = false;
    protected videoLoaded = false;
    protected autoplayBlocked = false;

    // Derived from Redux state
    // New model: when on hold, mainLeg is undefined, pipLeg has status 'hold'
    // When forwarding: pipLeg has status 'hold', mainLeg is the new outgoing/speaking call

    /** The active leg: mainLeg if exists, otherwise pipLeg if not on hold */
    private get activeLeg(): CallLeg | null {
        const s = this.callState;
        // mainLeg is the primary active leg
        if (s?.mainLeg) {
            return s.mainLeg;
        }
        // pipLeg only if it's not on hold
        if (s?.pipLeg && s.pipLeg.status !== 'hold') {
            return s.pipLeg;
        }
        return null;
    }

    /** The held leg (pipLeg when on hold) */
    private get heldLeg(): CallLeg | null {
        return this.callState?.pipLeg?.status === 'hold' ? this.callState.pipLeg : null;
    }

    /** The active session from the active leg */
    private get activeSession(): CallSession | null {
        const leg = this.activeLeg;
        if (!leg) return null;
        return leg.sessions.find(s => s.status === 'answered') ?? leg.sessions[0] ?? null;
    }

    protected get isCameraOn(): boolean {
        return this.callSessionManager.isCameraEnabled;
    }

    protected get isCameraAvailable(): boolean {
        return this.callSessionManager.isCameraAvailable;
    }

    protected get isMicMuted(): boolean {
        return this.callSessionManager.isMicMuted;
    }

    protected get callId(): string | null {
        return this.callState?.callId ?? null;
    }

    protected get sessionId(): string | null {
        return this.activeSession?.sessionId ?? null;
    }

    protected get calling(): boolean {
        return this.callState?.mainLeg?.status === 'outgoing';
    }

    protected get speaking(): boolean {
        return this.callState?.mainLeg?.status === 'speaking';
    }

    /** Forwarded: pipLeg is on hold AND mainLeg exists (outgoing/speaking to forward target) */
    protected get forwarded(): boolean {
        return this.callState?.pipLeg?.status === 'hold' && this.callState?.mainLeg != null;
    }

    /** On hold: pipLeg is on hold AND no mainLeg (waiting to forward or resume) */
    protected get onHold(): boolean {
        return this.callState?.pipLeg?.status === 'hold' && !this.callState?.mainLeg;
    }

    /** Bridging: both legs are being connected directly */
    protected get bridging(): boolean {
        return this.callState?.mainLeg?.status === 'bridging' || this.callState?.pipLeg?.status === 'bridging';
    }

    /** True if there's any active call (calling, speaking, on hold, forwarding, or bridging) */
    protected get hasActiveCall(): boolean {
        return this.calling || this.speaking || this.onHold || this.forwarded || this.bridging;
    }

    /** Outgoing call to a non-doorstation (unit/PBX) - media controls should be disabled */
    protected get isOutgoingToNonDoorstation(): boolean {
        return this.calling && this.remoteEntity?.kind !== CallEntityKind.Doorstation;
    }

    /** Whether to show the remote video (hide the placeholder overlay) */
    protected get shouldShowVideo(): boolean {
        if (!this.videoLoaded) return false;
        // Doorstation: show video as soon as it loads (even before answering)
        if (this.remoteEntity?.kind === CallEntityKind.Doorstation) return true;
        // On hold / bridging: keep curtain hidden
        if (this.onHold || this.bridging) return true;
        // Forwarding: hide curtain only once the forward target has answered (speaking),
        // keep the calling overlay visible while the forward call is still outgoing
        if (this.forwarded && !this.calling) return true;
        // Non-doorstation: show video when speaking or incoming
        return this.speaking || this.callState?.mainLeg?.status === 'incoming';
    }

    protected get remoteEntity(): CallEntity | null {
        return this.activeLeg?.entity ?? null;
    }

    protected get remoteEntityName(): string | null {
        return this.activeLeg?.entityName || null;
    }

    protected get remoteEntityOutputs(): DeviceOutput[] | undefined {
        return this.activeLeg?.entityOutputs;
    }

    protected get remoteEntityThingName(): string | null {
        return this.activeLeg?.entityThingName ?? null;
    }

    protected get heldEntity(): CallEntity | null {
        return this.heldLeg?.entity ?? null;
    }

    protected get heldEntityName(): string | null {
        return this.heldLeg?.entityName || null;
    }

    protected get entityIsV2(): boolean {
        return this.activeLeg?.entityIsV2 ?? false;
    }

    protected get isForwardDisabled(): boolean {
        let entitySupportCallV2 =
            this.remoteEntity?.kind !== CallEntityKind.Doorstation;

        if (!entitySupportCallV2) {
            entitySupportCallV2 = this.activeLeg?.entityIsV2 ?? this.isDeviceV2ByThingName(this.remoteEntityThingName ?? null);
        }

        return (
            this.sessionId == null ||
            !this.speaking ||
            this.forwarded ||
            !entitySupportCallV2
        );
    }

    /** Bridge enabled: pipLeg is on hold AND mainLeg is speaking (forward target answered) */
    protected get isBridgeEnabled(): boolean {
        return this.callState?.pipLeg?.status === 'hold'
            && this.callState?.mainLeg?.status === 'speaking';
    }

    constructor(screen: ScreenService, localizer: LocalizerService,
        private callSessionManager: CallSessionManager, private store: Store,
        private mediaStreamStore: MediaStreamStore) {
        super(screen, localizer);
    }

    ngOnInit(): void {
        this.subscribeToCallEvents();
        this.setupCallStateSubscription();

        this.mediaStreamStore.selectAutoplayBlocked().pipe(
            takeUntil(this.destroy$)
        ).subscribe(blocked => this.autoplayBlocked = blocked);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['entityId'] && !changes['entityId'].firstChange) {
            this.videoLoaded = false;
            this.isAudioMuted = false;
            this.callState = null;

            // Tear down entity-specific subscriptions and re-create them
            this.entitySub$.next();
            this.setupCallStateSubscription();
            if (this.viewInitialized) {
                this.setupVideoSubscriptions();
            }
        }
    }

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        this.setupVideoSubscriptions();
    }

    private setupCallStateSubscription(): void {
        this.store.select(selectCurrentCall).pipe(
            takeUntil(this.entitySub$),
            takeUntil(this.destroy$)
        ).subscribe(state => {
            const prevState = this.callState;

            const isForThisEntity = !state?.destinationId || state.destinationId === this.entityId;
            this.callState = isForThisEntity ? state : null;

            console.log("[DeviceLiveView] Redux state updated:", {
                entityId: this.entityId,
                destinationId: state?.destinationId,
                isForThisEntity,
                callId: state?.callId,
                mainLeg: state?.mainLeg ? { status: state.mainLeg.status, entity: state.mainLeg.entity } : null,
                pipLeg: state?.pipLeg ? { status: state.pipLeg.status, entity: state.pipLeg.entity } : null,
            });

            this.onStatusChanged(prevState, this.callState);
        });
    }

    private setupVideoSubscriptions(): void {
        // Main video stream per-call: switch stream when callId changes
        this.store.select(selectCurrentCall).pipe(
            map(state => {
                const isForThisEntity = !state?.destinationId || state.destinationId === this.entityId;
                return isForThisEntity ? state?.callId ?? null : null;
            }),
            distinctUntilChanged(),
            switchMap(callId => this.mediaStreamStore.selectCallVideoStream(callId)),
            takeUntil(this.entitySub$),
            takeUntil(this.destroy$)
        ).subscribe(stream => {
            if (this.videoRef?.nativeElement) {
                this.videoRef.nativeElement.srcObject = stream;
            }
        });

        // Second video stream (forward/pip target)
        this.mediaStreamStore.selectSecondVideoStream().pipe(
            takeUntil(this.entitySub$),
            takeUntil(this.destroy$)
        ).subscribe(stream => {
            if (this.secondVideoRef?.nativeElement) {
                this.secondVideoRef.nativeElement.srcObject = stream;
            }
        });
    }

    private onStatusChanged(prevState: CallEntry | null, newState: CallEntry | null): void {
        // Call is active if either mainLeg or pipLeg exists
        const wasActive = prevState?.mainLeg != null || prevState?.pipLeg != null;
        const isActive = newState?.mainLeg != null || newState?.pipLeg != null;
        if (wasActive && !isActive) {
            // Call ended (went from having any leg to having none = idle)
            // this.onCallEnd();
        }
    }

    ngOnDestroy(): void {
        this.entitySub$.next();
        this.entitySub$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }

    //#region Exposed Methods (called from dashboards)

    /**
     * Handle an incoming call session
     *
     * @param session - The incoming call session
     * @param answer - Whether to auto-answer the call
     * @param callee - The remote entity calling (Doorstation, Unit, etc.)
     */
    handleIncomingCallSession(
        session: Session,
        answer: boolean = false,
        callee: CallParticipant
    ): void {
        this.closeCall();

        console.log("Handling incoming call session:", session, callee);

        // Build caller (this entity - PBX or Unit)
        const caller: CallParticipant = {
            id: this.entityId!,
            kind: this.entityKind!
        };

        // Get isV2 from shadow if not provided
        const calleeWithIsV2: CallParticipant = {
            ...callee,
            isV2: callee.isV2 ?? this.isDeviceV2ByThingName(callee.thingName ?? null)
        };

        this.callSessionManager.startCall(caller, calleeWithIsV2, this.installationId!, session.callId);

        if (answer) {
            this.startDeviceTalkSession();
        }
    }

    private getDefaultEntityName(entity: CallEntity): string {
        if (entity.kind === "pbx") {
            return this.localizer.getLocalizedString("General.Pbx");
        } else if (entity.kind === "unit") {
            return this.localizer.getLocalizedString("General.Unit");
        }
        return "";
    }

    /**
     * Start an outgoing call to a remote entity (device, unit, etc.)
     *
     * @param callee - The remote entity to call
     */
    startOutgoingCall(callee: CallParticipant): void {
        // Close any existing call first
        this.closeCall();

        // Get isV2 from shadow if not provided
        const isV2 = callee.isV2 ?? this.isDeviceV2ByThingName(callee.thingName ?? null);

        // Build caller (this entity - PBX or Unit)
        const caller: CallParticipant = {
            id: this.entityId!,
            kind: this.entityKind!
        };

        // Build callee with isV2
        const calleeWithIsV2: CallParticipant = {
            ...callee,
            isV2
        };

        // Start the actual call via CallSessionManager
        this.callSessionManager.startCall(caller, calleeWithIsV2, this.installationId!);
    }

    private isDeviceV2ByThingName(thingName: string | null): boolean {
        if (!thingName || this.shadows?.length <= 0) {
            return false;
        }

        const shadow = this.shadows.find(u => u.thingName === thingName)?.shadow;
        const applicationVersion = shadow?.device?.version?.application;

        if (!applicationVersion) return false;
        return new Versioning(applicationVersion).compareTo(v2CallVersion) >= 0;
    }

    closeCall(): void {
        if (this.callId == null) {
            return;
        }

        this.callSessionManager.closeMainLeg(this.callId);
    }

    //#endregion Exposed Methods

    protected toggleFullscreen(): void {
        const videoElement = this.videoRef?.nativeElement;

        if (!videoElement) {
            return;
        }

        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            videoElement.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable fullscreen: ${err.message}`);
            });
        }
    }

    protected toggleAudio(): void {
        this.isAudioMuted = !this.isAudioMuted;

        if (this.isAudioMuted) {
            return this.callSessionManager.muteSessionAudio(this.callId!, this.sessionId!);
        }

        this.callSessionManager.unmuteSessionAudio(this.callId!, this.sessionId!);
    }

    protected toggleMic(): void {
        if (this.isMicMuted) {
            this.callSessionManager.unmuteMicrophone(this.callId!, this.sessionId!);
        } else {
            this.callSessionManager.muteMicrophone(this.callId!, this.sessionId!);
        }
    }

    protected toggleCamera(): void {
        if (!this.callId || !this.sessionId) return;
        if (this.isCameraOn) {
            this.callSessionManager.disableCamera(this.callId, this.sessionId);
        } else {
            this.callSessionManager.enableCamera(this.callId, this.sessionId);
        }
    }

    protected onLoad(): void {
        this.videoLoaded = true;
    }

    protected resumeAudio(): void {
        if (this.callId) {
            this.callSessionManager.resumeAudio(this.callId);
            this.mediaStreamStore.setAutoplayBlocked(false);
        }
    }

    protected startDeviceTalkSession(): void {
        if (!this.sessionId)
            return;

        this.callSessionManager.startDeviceTalkSession(this.callId!, this.sessionId!);
    }

    protected onCloseButtonClick = (): void => this.closeCall();

    protected onOutputClick(output: DeviceOutput): void {
        if (!this.remoteEntity?.id) {
            return;
        }
        const deviceId = this.remoteEntity.id!;
        const unitId = this.entityId!;
        const installationId = this.installationId!;
        const outputAddress = output.address;
        this.callSessionManager.sendAction(deviceId, unitId, installationId, outputAddress);
    }

    protected hold(): void {
        
        this.callSessionManager.hold(this.callId!);
    }

    protected resume(): void {
        this.callSessionManager.resume(this.callId!);
    }

    protected bridge(): void {
        if (!this.callId) return;
        this.callSessionManager.bridge(this.callId);
    }

    private onCallEnd(): void {
        console.log("On call end");

        this.videoLoaded = false;
        this.isAudioMuted = false;

        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    }

    private isDeviceV2(): boolean {
        if (!this.remoteEntity || this.shadows?.length <= 0)
            return false;

        const shadow = this.shadows.find(
            u => u.thingName == this.remoteEntityThingName!
        )?.shadow;

        const applicationVersion = shadow?.device?.version?.application;

        if (!applicationVersion) return false;
        return new Versioning(applicationVersion).compareTo(v2CallVersion) >= 0;
    }

    private subscribeToCallEvents(): void {
        this.callSessionManager.onCallEnd().pipe(
            takeUntil(this.destroy$)
        ).subscribe(callSession => {
            console.log("On call end", callSession);
            if (callSession.callId !== this.callId) {
                return;
            }
            
            const message = this.localizer.getLocalizedString("Call.CallEndMessage", {device: this.remoteEntityName});
            this.displayWarningNotification(message);
        });

        this.callSessionManager.onCallStop().pipe(
            takeUntil(this.destroy$)
        ).subscribe(callSession => {
            console.log("On call stop", callSession);
            if (callSession.callId !== this.callId) {
                return;
            }
        });

        this.callSessionManager.onCallInitError().pipe(
            takeUntil(this.destroy$)
        ).subscribe(callSession => {
            if (callSession.callId !== this.callId) {
                return;
            }
            const message = this.localizer.getLocalizedString("Call.CallEndMessage", {device: this.remoteEntityName});
            this.displayErrorNotification(message);
        });
    }

    protected readonly CallEntityKind = CallEntityKind;
}
